<template>
  <div>
    <p>
      All fields highlighted in red will require you to click into each field to confirm the details are correct to proceed with your online
      application.
    </p>
    <QForm @submit.prevent="onSubmitNext">
      <SubHeader header="Your Details" />
      <div class="row">
        <InputField label="First Name" placeholder="First Name" v-model="form.firstName" :required="true" :autoFocus="true" />
        <InputField label="Middle Name" placeholder="Middle Name" v-model="form.middleName" />
        <InputField label="Last Name" placeholder="Last Name" v-model="form.lastName" :required="true" />
        <div class="q-pa-xs col-12 col-sm-6">
          <p class="field-label">Date of Birth*</p>
          <!-- <EUIDateInput
            dense
            dateMask="DD/MM/YYYY"
            hide-bottom-space
            color="primary"
            v-model="$v.form.dob.$model"
            placeholder="----/--/--"
            :error="$v.form.dob.$error"
            @focus="onFocus"
            alt="dob"
          /> -->
          <EUIDateInput
            dense
            dateMask="DD/MM/YYYY"
            hide-bottom-space
            color="primary"
            placeholder="----/--/--"
            v-model="form.dob"
            @blur="dobBlur"
          />
        </div>
      </div>
      <br />
      <br />
      <SubHeader header="Address" />
      <div class="row">
        <div class="q-pa-xs col-12 col-sm-6">
          <label class="field-label">Current Residential Address*</label>
          <EUIAutocomplete
            class="primary col-6"
            theme="primary"
            popup-content-class="sai-select-popup"
            :country-restrictions="['au', 'nz']"
            behavior="menu"
            dropdown-icon="eva-search-outline"
            no-error-icon
            hide-bottom-space
            :disable="false"
            :clearable="false"
            @input="updatePlace"
            :style="getStyles"
            :rules="getRules()"
            ref="address"
          />
        </div>
        <div class="q-pa-xs col-12 col-sm-6">
          <label class="field-label" style="display: inline-block">Previous Residential Address</label>
          <img
            src="/assets/icons/small-info.svg"
            :style="convertColourComputed ? convertColourComputed : ''"
            alt="Please provide your previous residential address if you have have lived in the current residential address for less than three years."
            title="Please provide your previous residential address if you have have lived in the current residential address for less than three years."
            style="cursor: pointer; vertical-align: middle; margin: 0 0 0 5px; color: var(--primary-color)"
            @click="openDialog"
          />
          <EUIAutocomplete
            class="primary col-6"
            theme="primary"
            popup-content-class="sai-select-popup"
            :country-restrictions="['au', 'nz']"
            behavior="menu"
            dropdown-icon="eva-search-outline"
            no-error-icon
            hide-bottom-space
            :disable="false"
            :clearable="false"
            @input="updatePlace2"
          />
        </div>
      </div>
      <br />
      <br />
      <SubHeader :header="region == 'NZ' ? 'New Zealand Driver\'s Licence' : 'Australian Driver\'s Licence'" />
      <div class="row">
        <InputField label="Drivers Licence No." placeholder="Drivers Licence No." v-model="form.driversLicenceNumber" :required="true" />
        <div class="q-pa-xs col-12 col-sm-6">
          <p class="field-label">Expiry*</p>
          <EUIDateInput
            dense
            dateMask="DD/MM/YYYY"
            v-model="form.driversLicenceExpiry"
            hide-bottom-space
            color="primary"
            placeholder="----/--/--"
          />
        </div>
        <InputField
          v-if="region == 'AU'"
          label="State/Territory"
          placeholder="State/Territory"
          v-model="form.driversLicenceState"
          :required="true"
          type="auState"
          @blur="blurHandler"
        />
        <InputField
          v-if="region == 'NZ'"
          label="Version"
          placeholder="Version"
          v-model="form.version"
          :required="region == 'NZ' ? true : false"
        />
        <InputCardField
          v-else
          label="Card No."
          placeholder="Card No."
          :state="drivingLicenseState"
          v-model="form.driversCardNumber"
          :required="true"
          ref="card"
        />
      </div>
      <div class="q-px-md q-py-md text-center">
        <QBtn no-caps @click="onRedo">Back</QBtn>
        <QBtn color="primary" type="submit" no-caps :loading="loadingOnNext" style="margin-left: 25px">Next</QBtn>
      </div>
    </QForm>
    <IDScanConfirmDialog
      v-if="showIDScanConfirmDialog"
      @closeIDScanConfirmDialog="closeIDScanConfirmDialog"
      @submitIDScanConfirmDialog="submitIDScanConfirmDialog"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { QBtn, QForm } from 'quasar';
import { date, EnGbLang, EUIAutocomplete } from 'eclipx.ui';
import SubHeader from '@/components/Atoms/SubHeader';
import InputField from '@/components/Atoms/InputFieldForceCheck.vue';
import InputCardField from '@/components/Atoms/inputLicenseCardValidator.vue';
import EUIDateInput from '@/components/Atoms/EUIDateInputForceCheck.vue';
import MyCustomFunctions from '@/mixins/MyCustomFunctions.js';
import MutateCreateOrUpdateProperty from '@/graphql/mutations/MutateCreateOrUpdateProperty';
import MutateSubmitCreditCheck from '@/graphql/mutations/MutateSubmitCreditCheck';
import { GLOBAL_ERROR } from '@/components/GlobalError/GlobalError';
import { GlobalDialog } from '@/components/Dialogs/GlobalDialog';
import IDScanConfirmDialog from '@/components/Molecules/IDScanConfirmDialog.vue';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import ColoureMixins from '@/mixins/ColourMixins.js';

export default {
  name: 'CalculatorIDVerifForm',
  mixins: [MyCustomFunctions, ColoureMixins],
  components: {
    InputField,
    EUIDateInput,
    QBtn,
    QForm,
    SubHeader,
    EUIAutocomplete,
    IDScanConfirmDialog,
    InputCardField
  },
  props: ['makeHeaderActive', 'enable'],
  computed: {
    ...mapGetters({
      getPrimaryApplicantIDScanData: 'vehicleApplication/getPrimaryApplicantIDScanData',
      getSecondaryApplicantIDScanData: 'vehicleApplication/getSecondaryApplicantIDScanData',
      isUserPrimaryApplicant: 'vehicleApplication/isUserPrimaryApplicant'
    }),
    ...mapState({
      scoreboard: (state) => state.vehicleApplication.vehicleApplication.scoreboard
    }),
    region() {
      return this.scoreboard?.currentUser?.region ? this.scoreboard?.currentUser?.region : 'AU';
    },
    personalDetails() {
      if (this.isUserPrimaryApplicant) return this.getPrimaryApplicantIDScanData;
      else return this.getSecondaryApplicantIDScanData;
    },
    creditCheckStatus() {
      if (this.scoreboard && this.scoreboard.statusCreditCheck) return this.scoreboard.statusCreditCheck.toLowerCase();
      else return 'refer';
    },
    convertColourComputed() {
      return this.convertColour();
    },
    getStyles() {
      if (!this.form.currentAddress) {
        return {
          'border': '1.5px solid red',
          'border-radius': '5px 5px'
        };
      } else return null;
    },
    drivingLicenseState() {
      return this.form.driversLicenceState ? this.form.driversLicenceState.toUpperCase().trim() : null;
    }
  },
  created() {
    if (this.personalDetails) {
      this.form.firstName = this.personalDetails.firstName;
      this.form.middleName = this.personalDetails.middleName;
      this.form.lastName = this.personalDetails.lastName;

      let dob = this.personalDetails.dateOfBirth
        ? date.extractDate(this.personalDetails.dateOfBirth, 'YYYY-MM-DD', EnGbLang.date)
        : new Date(Date.now());
      this.form.dob = date.formatDate(dob, 'DD/MM/YYYY', EnGbLang.date);

      let expiry = this.personalDetails.licenseExpiry
        ? date.extractDate(this.personalDetails.licenseExpiry, 'YYYY-MM-DD', EnGbLang.date)
        : new Date(Date.now());
      this.form.driversLicenceExpiry = date.formatDate(expiry, 'DD/MM/YYYY', EnGbLang.date);
      this.form.driversLicenceNumber = this.personalDetails.licenseNumber;
      this.form.driversCardNumber = this.personalDetails.cardNumber;
      this.form.driversLicenceState = this.personalDetails.licenseIssuedState;
    }
  },
  data: function () {
    return {
      form: {
        firstName: null,
        lastName: null,
        middleName: null,
        dob: null,
        streetNo: null,
        streetName: null,
        suburb: null,
        state: null,
        postCode: null,
        country: null,
        countryCode: null,
        subPremise: null,
        currentAddress: null,
        previousAddress: null,
        version: null,
        driversLicenceNumber: null,
        driversCardNumber: null,
        driversLicenceExpiry: null,
        driversLicenceState: null
      },
      showIDScanConfirmDialog: false,
      loadingOnNext: false
    };
  },
  methods: {
    updatePlace(place) {
      if (place && place.address) {
        this.form.streetNo = place.address?.streetNumber || place.address?.address;
        this.form.streetName = place.address.address;
        this.form.suburb = place.address.suburb;
        this.form.state = place.address.state;
        this.form.postCode = place.address.postCode;
        this.form.country = place.address.country;
        this.form.countryCode = place.address.countryCode;
        this.form.subPremise = place.address.subPremise;
        this.form.currentAddress = place.label;
        //this.$refs.address.$parent.validate();
      }
    },
    updatePlace2(place) {
      if (place && place.address) this.form.previousAddress = place.label;
    },
    openDialog: function () {
      this.$euiNotifications.emit({
        notification: GlobalDialog,
        body: '<span style="color:white">Please provide your previous residential address if you have have lived in the current residential address for less than three years.</span>',
        qCardStyle: {
          padding: '0',
          width: '400px',
          maxWidth: '80vw',
          backgroundColor: 'var(--primary-color)'
        },
        closeBtnStyle: {
          color: 'white'
        }
      });
    },
    closeIDScanConfirmDialog: async function () {
      this.showIDScanConfirmDialog = false;
    },
    submitIDScanConfirmDialog: async function () {
      this.showIDScanConfirmDialog = false;
      await this.onSubmit();
    },
    onSubmitNext: async function () {
      if (!this.form.currentAddress) {
        this.$refs.address.$el.focus();
        return;
      }
      this.showIDScanConfirmDialog = true;
    },
    onSubmit: async function () {
      this.loadingOnNext = true;

      const response = await this.submit();

      if (response?.data?.createOrUpdateProperty?.status === '200') {
        this.updateVehicleApplicationState({
          value: this.form,
          propertyFirst: this.isUserPrimaryApplicant ? 'primaryApplicant' : 'secondaryApplicant',
          propertySecond: 'idScanResult'
        });
        if (this.isUserPrimaryApplicant) await this.updateVehicleApplication('completed', 'primaryApplicant', 'identityVerificationStatus');
        else await this.updateVehicleApplication('completed', 'secondaryApplicant', 'identityVerificationStatus');

        if (!this.isUserPrimaryApplicant && this.creditCheckStatus === 'waiting') {
          await this.onSubmitCreditCheck();
        }

        setTimeout(() => {
          this.$emit('onSubmitNext');
        });
      }

      this.loadingOnNext = false;
    },
    onSubmitCreditCheck: async function () {
      await this.$apollo
        .mutate({
          mutation: MutateSubmitCreditCheck,
          variables: {
            payload: {
              applicationId: this.$store.getters['vehicleApplication/vehicleApplication']
                ? this.$store.getters['vehicleApplication/vehicleApplication'].applicationId
                : null,
              testingValue: 'testingValue'
            }
          }
        })
        .then(async (data) => {
          if (data?.data?.submitCreditCheck?.result) {
            var resultValue = 'refer';
            await this.updateVehicleApplication(resultValue, 'statusCreditCheck');
          }
        })
        .catch((error) => {
          let gqlError = error.graphQLErrors[0];
          this.error = gqlError ? gqlError.message : 'Error';
          if (error) {
            this.$euiNotifications.emit({
              notification: GLOBAL_ERROR
            });
          }
        });
    },
    onRedo: async function () {
      setTimeout(() => {
        this.$emit('onRedo');
      });
    },
    submit: async function () {
      return await this.$apollo
        .mutate({
          mutation: MutateCreateOrUpdateProperty,
          variables: {
            applicationId: this.$store.getters['vehicleApplication/vehicleApplication']
              ? this.$store.getters['vehicleApplication/vehicleApplication'].applicationId
              : null,
            params: {
              value: 'idScanResult',
              idScanResult: this.form,
              propertyFirst: this.isUserPrimaryApplicant ? 'primaryApplicant' : 'secondaryApplicant',
              propertySecond: 'idScanResult'
            }
          }
        })
        .catch((error) => {
          let gqlError = error.graphQLErrors[0];
          this.error = gqlError ? gqlError.message : 'Error';
          if (error) {
            this.$euiNotifications.emit({
              notification: GLOBAL_ERROR
            });
          }
        });
    },
    getRules: function () {
      const rules = [];
      rules.push((val) => {
        if (!val || val == '') {
          return 'Please enter your details';
        }
        return true;
      });
      return rules;
    },
    dobBlur() {
      this.$refs.address.$refs.QSelect.focus();
    },
    blurHandler() {
      this.$refs.card.$refs.field.validate();
    }
  }
};
</script>
<style lang="sass" scoped>
.EUISelect::v-deep
  input::placeholder
    color: #333333!important
</style>
